import React, { useState, useEffect } from "react";
import {
  BsFillArrowRightCircleFill,
  BsFillArrowLeftCircleFill,
} from "react-icons/bs";
import { useLanguageContext } from "../../Context/LanguageContext";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../helpers/strictModeDragDropHelper";
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  InputBase,
  Box,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";

const InspectionReportAttributeSelector = ({
  availableAttributes,
  selectedAttributes,
  onSelectionChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { localization } = useLanguageContext();

  useEffect(() => {
    setSearchQuery("");
  }, [availableAttributes]);

  const handleAddAttribute = (attributeToAdd) => {
    const newSelected = [...selectedAttributes, attributeToAdd];
    onSelectionChange(newSelected);
  };

  const handleRemoveAttribute = (attributeToRemove) => {
    const newSelected = selectedAttributes.filter(
      (attr) => attr.id !== attributeToRemove.id,
    );
    onSelectionChange(newSelected);
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selectedAttributes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onSelectionChange(items);
  };

  const filteredAvailableAttributes = availableAttributes.filter((attribute) =>
    attribute.nameDisplay
      .toLowerCase()
      .startsWith(searchQuery.trim().toLowerCase()),
  );

  return (
    <Box className="container-fluid attribute-selector-box">
      <div className="row attribute-selector-row">
        {/* Left Panel: Available Attributes */}
        <div className="col-md-6 p-0 h-100">
          <Paper elevation={0} className="panel-paper left-panel-paper">
            <Paper component="form" className="search-paper">
              <SearchIcon className="search-icon" />
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={localization.search}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </Paper>
            <List dense className="attribute-list left-panel-list">
              {filteredAvailableAttributes.map((attribute) => (
                <ListItem
                  key={attribute.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      title={localization.moveAttributeRight}
                      onClick={() => handleAddAttribute(attribute)}
                    >
                      <BsFillArrowRightCircleFill
                        size={22}
                        className="svgIcon"
                      />
                    </IconButton>
                  }
                  className="attribute-list-item"
                >
                  <ListItemText primary={attribute.nameDisplay} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </div>

        {/* Right Panel: Selected Attributes */}
        <div className="col-md-6 p-0 h-100">
          <Paper elevation={0} className="panel-paper">
            <Typography variant="h6" gutterBottom component="div">
              {localization.selectedAttributes}
            </Typography>
            {selectedAttributes.length === 0 ? (
              <div className="no-attributes-message">
                <Typography color="textSecondary">
                  {localization.selectAttribute}
                </Typography>
              </div>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {localization.dragDropItemText}
                </Typography>
                <DragDropContext onDragEnd={handleOnDragEnd}>
                  <StrictModeDroppable droppableId="selectedAttributes">
                    {(provided) => (
                      <List
                        dense
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="attribute-list"
                      >
                        {selectedAttributes.map((attribute, index) => (
                          <Draggable
                            key={attribute.id}
                            draggableId={String(attribute.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    title={localization.moveAttributeLeft}
                                    onClick={() =>
                                      handleRemoveAttribute(attribute)
                                    }
                                  >
                                    <BsFillArrowLeftCircleFill
                                      size={22}
                                      className="svgIcon"
                                    />
                                  </IconButton>
                                }
                                className="attribute-list-item"
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? "#e0e0e0"
                                    : "#f5f5f5",
                                }}
                              >
                                <DragIndicatorIcon className="drag-indicator-icon" />
                                <ListItemText primary={attribute.nameDisplay} />
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </StrictModeDroppable>
                </DragDropContext>
              </>
            )}
          </Paper>
        </div>
      </div>
    </Box>
  );
};

export default InspectionReportAttributeSelector;
